import React, { useState } from 'react'
import {
  adCreativeService,
  leadCardService,
  useGetBusiness,
  useGetBusinessID,
} from '@marketing-milk/frontend'
import { AdCreative, LeadCard, LeadCardCreateDTO } from '@marketing-milk/interfaces'
import { Drawer, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { EmptyLeadCardId } from 'components'
import { LeadCardPreview } from 'pages/business/lead-cards/lead-card-preview/LeadCardPreview'
import { leadCardDTOWire } from 'pages/business/lead-cards/wizard/lead-card.wire'
import { LeadCardWizard } from 'pages/business/lead-cards/wizard/LeadCardWizard'
import { toast } from 'react-toastify'
import { AdsByLeadForm } from './AdsByLeadForm'
import { MoveToFormBar } from './MoveToFormBar'
import { useLeadCardCandidates } from './useLeadCardCandidates'

interface Props {
  isOpen: boolean
  onClose: () => void
  campaignName?: string
  creatives?: AdCreative[]
  onAssigned: () => void | Promise<unknown>
  // Keep newly created forms available even if their first assignment fails.
  rememberedForms?: LeadCard[]
  rememberForms: (forms: LeadCard[]) => void
}

const dedupeById = (forms: LeadCard[]): LeadCard[] => {
  const seen = new Set<number>()
  return forms.filter(form => {
    if (seen.has(form.id)) return false
    seen.add(form.id)
    return true
  })
}

const plural = (n: number) => `${n} ad${n === 1 ? '' : 's'}`

export const EditLeadFormsModal = ({
  isOpen,
  onClose,
  campaignName,
  creatives: creativesProp,
  onAssigned,
  rememberedForms,
  rememberForms,
}: Props) => {
  const businessID = Number(useGetBusinessID())
  const business = useGetBusiness()
  const creatives = creativesProp ?? []
  const { leadCards, truncated } = useLeadCardCandidates(businessID)
  // Sorted by name so sections hold still when an assignment changes which ad uses a form first.
  const campaignForms = dedupeById(
    creatives.map(c => c.leadCardUsed).filter((form): form is LeadCard => Boolean(form))
  ).sort((a, b) => a.formName.localeCompare(b.formName))
  // Campaign forms come first in the assignment picker, followed by other available forms.
  const forms = dedupeById([...campaignForms, ...(rememberedForms ?? []), ...(leadCards ?? [])])
  // IDs rather than ads, so everything reading the selection sees each ad's current form.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const selectedCreatives = creatives.filter(c => selectedIds.has(c.id))
  const [previewFormId, setPreviewFormId] = useState<number>()
  const previewForm = forms.find(f => f.id === previewFormId) ?? campaignForms[0]
  const [busyMessage, setBusyMessage] = useState<string>()
  const busy = !!busyMessage
  const [building, setBuilding] = useState<'copy' | 'blank' | false>(false)
  const [formActionsContainer, setFormActionsContainer] = useState<HTMLDivElement | null>(null)

  const selectCreative = (creative: AdCreative) => {
    if (busy) return
    setSelectedIds(selected => {
      const next = new Set(selected)
      if (next.has(creative.id)) next.delete(creative.id)
      else next.add(creative.id)
      return next
    })
  }

  const setSelected = (ads: AdCreative[], select: boolean) => {
    if (busy) return
    setSelectedIds(selected => {
      const next = new Set(selected)
      ads.forEach(c => (select ? next.add(c.id) : next.delete(c.id)))
      return next
    })
  }

  const handleClose = () => {
    if (busy) return
    setSelectedIds(new Set())
    setBuilding(false)
    onClose()
  }

  // The endpoint applies to every assignable creative and reports the rest by name rather than
  // rejecting the whole batch — one published ad must not block moving the other nineteen. This
  // surfaces exactly which ones didn't land, since the sections alone won't say why.
  const notifyFailures = (failed: Array<{ name: string }>) => {
    if (!failed.length) return
    toast.error(
      `${failed.length} published ad${failed.length === 1 ? '' : 's'} couldn't be changed: ${failed
        .map(f => f.name)
        .join(', ')}`
    )
  }

  // Everything below writes the FK on the creative, so it is global rather than scoped to this
  // campaign. Only the ads that didn't land stay selected afterwards, so a retry acts on exactly
  // what's still outstanding.
  const run = async (
    message: string,
    write: () => Promise<{ updated: AdCreative[]; failed: Array<{ name: string }> }>
  ) => {
    if (busy) return
    setBusyMessage(message)
    try {
      const { updated, failed } = await write()
      notifyFailures(failed)
      await onAssigned()
      const updatedIds = new Set(updated.map(c => c.id))
      setSelectedIds(selected => new Set(Array.from(selected).filter(id => !updatedIds.has(id))))
      return updated
    } catch (err: any) {
      toast.error(err?.message ? err.message : err)
    } finally {
      setBusyMessage(undefined)
    }
  }

  const move = async (ads: AdCreative[], form: LeadCard | null) => {
    const leadCardID = form?.id ?? EmptyLeadCardId
    if (form) setPreviewFormId(form.id)
    const targets = leadCardID === EmptyLeadCardId ? ads.filter(c => c.leadCardUsed) : ads
    if (targets.length === 0) return
    const updated = await run(
      leadCardID === EmptyLeadCardId ? 'Removing lead forms…' : 'Assigning lead forms…',
      () =>
        adCreativeService.bulkAssignLeadCard(
          businessID,
          targets.map(c => c.id),
          leadCardID
        )
    )
    if (!updated?.length) return
    toast.success(
      form
        ? `${plural(updated.length)} assigned to "${form.formName}"`
        : `Took the lead form off ${plural(updated.length)}`
    )
  }

  const aiSort = async (ads: AdCreative[], candidates: LeadCard[]) => {
    if (ads.length === 0 || candidates.length < 2) return
    let skipped: Array<{ name: string }> = []
    const updated = await run('Sorting ads with AI…', async () => {
      const result = await adCreativeService.aiSortLeadCards(
        businessID,
        ads.map(c => c.id),
        candidates.map(f => f.id)
      )
      skipped = result.skipped
      return result
    })
    if (updated?.length) toast.success(`AI sorted ${plural(updated.length)} onto their forms`)
    if (skipped.length) {
      toast.warn(
        `AI couldn't match ${plural(skipped.length)}. Their lead forms were left unchanged; ` +
          `review: ${skipped.map(c => c.name).join(', ')}`
      )
    }
  }

  const createForm = async (leadCardDTO: LeadCardCreateDTO) => {
    setBusyMessage('Creating lead form…')
    let newLeadCard: LeadCard
    try {
      newLeadCard = await leadCardService.create(businessID, { ...leadCardDTO })
    } finally {
      setBusyMessage(undefined)
    }
    rememberForms(dedupeById([...(rememberedForms ?? []), newLeadCard]))
    setPreviewFormId(newLeadCard.id)
    setBuilding(false)
    toast.success(`"${newLeadCard.formName}" created`)
    await move(selectedCreatives, newLeadCard)
  }

  // A stray Escape or backdrop click mid-build would throw the form away; the X still closes.
  return (
    <Drawer anchor="right" open={isOpen} onClose={building ? undefined : handleClose}>
      <fieldset
        disabled={busy}
        aria-busy={busy}
        className="m-0 h-full w-screen min-w-0 max-w-full border-0 p-0 xl:w-[80vw]"
      >
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center gap-3 border-b border-mm-neutral-1-200 bg-white px-6 py-2">
            <h2 className="min-w-0 flex-1 truncate text-base font-bold text-mm-neutral-1-900">
              {campaignName}
            </h2>
            <span className="flex-shrink-0 text-sm text-mm-neutral-1-500">
              {building ? 'New lead form' : 'Lead forms'}
            </span>
            <IconButton onClick={handleClose} aria-label="Close" size="small">
              <CloseIcon />
            </IconButton>
          </div>

          <div className="flex-grow overflow-y-auto overflow-x-hidden bg-mm-neutral-2-50 p-4">
            {building && (
              <>
                <p className="mb-3 text-sm text-mm-neutral-1-600">
                  Saving creates this form and assigns it to your {plural(selectedCreatives.length)}
                  . Cancel to return to your selection.
                </p>
                <LeadCardWizard
                  onSave={createForm}
                  startFromForms={building === 'copy' ? forms : undefined}
                  saveLabel={`Create form & assign to ${plural(selectedCreatives.length)}`}
                  actionsContainer={formActionsContainer}
                  onCancelCreation={() => setBuilding(false)}
                />
              </>
            )}
            {/* Hidden rather than unmounted while building, so cancelling keeps the filter and
                collapsed sections as well as the selection. */}
            <div className={building ? 'hidden' : 'flex flex-col items-start gap-6 lg:flex-row'}>
              <div className="w-full min-w-0 flex-1 space-y-4">
                <AdsByLeadForm
                  creatives={creatives}
                  declaredForms={campaignForms}
                  selectedIds={selectedIds}
                  selectCreative={selectCreative}
                  setSelected={setSelected}
                  onPreview={form => setPreviewFormId(form.id)}
                />
              </div>

              {/* Sticky beside the rows so a form can be checked against the ads sitting on
                    it. On the right to match the lead-card builder, which is form-left
                    preview-right. */}
              {previewForm && (
                <aside className="w-full lg:sticky lg:top-0 lg:w-[360px] lg:flex-shrink-0">
                  <div className="rounded-lg border border-mm-neutral-1-200 bg-white p-3">
                    <p className="mb-2 break-words text-sm font-semibold text-mm-neutral-1-600">
                      {previewForm.formName}
                    </p>
                    <LeadCardPreview
                      leadCard={leadCardDTOWire(previewForm)}
                      business={business}
                      mobilePreview={false}
                      selectionPreview={false}
                      showName={false}
                    />
                  </div>
                </aside>
              )}
            </div>
          </div>

          <footer className="flex-none border-t border-mm-neutral-1-200 bg-white px-6 py-3">
            {busyMessage && (
              <p role="status" className="mb-2 text-sm font-semibold text-mm-primary-700">
                {busyMessage}
              </p>
            )}
            {building ? (
              <div ref={setFormActionsContainer} />
            ) : (
              <MoveToFormBar
                selected={selectedCreatives}
                forms={forms}
                campaignForms={campaignForms}
                loadingForms={!leadCards}
                truncated={truncated}
                canCreate={!!business?.fbPageID}
                onBuild={setBuilding}
                onMove={form => move(selectedCreatives, form)}
                onAISort={aiSort}
                onClearSelection={() => setSelectedIds(new Set())}
                onDone={handleClose}
              />
            )}
          </footer>
        </div>
      </fieldset>
    </Drawer>
  )
}
